import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { track } from "../../util/tealium";
import defaultContent from "../../../content/keyMetrics";
import { FocusTrap } from "../../components/FocusTrap/FocusTrap";
import LeafPanel from "../../leaf/LeafPanel/LeafPanel";
import { IconClose } from "../../icons/IconClose/IconClose";
import TEALIUM from "../../../common/enums/tealium-enums";
import "./KpiSelector.scss";
import useWindowSize from "../../hooks/useWindowSize";
import breakpoints from "../../scss/breakpoints.module.scss";
import { IconCloseThick } from "../../icons/IconCloseThick/IconCloseThick";
import { Button } from "../Button/Button";
import {
  closestCenter,
  defaultDropAnimationSideEffects,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { KpiItem, SortableKpiItem } from "./SortableKpiItem";

type Props = {
  show: boolean;
  availableKpis: string[];
  selectedKpis: string[];
  minSelectionsAllowed: number;
  maxSelectionsAllowed: number;
  onSubmitFn: (selectedKpis: string[]) => void;
  onCloseCallbackFn: () => void;
  description?: string;
  textOverride?: { [key: string]: string };
  personalPVRequirementCode?: string;
  disableScrollLock?: boolean;
};
export const KpiSelector = ({
  show,
  availableKpis,
  selectedKpis,
  minSelectionsAllowed,
  maxSelectionsAllowed,
  onSubmitFn,
  onCloseCallbackFn,
  description,
  textOverride,
  personalPVRequirementCode,
  disableScrollLock = false,
}: Props) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(["keyMetrics"]);
  const [selectedKpiList, setSelectedKpiList] = useState(selectedKpis);
  const [items, setItems] = useState<string[]>(availableKpis);
  const [activeId, setActiveId] = useState<string>("");
  const [focusedElement, setFocusedElement] = useState(null as unknown as HTMLElement);
  const { width } = useWindowSize();
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const isKpiSelected = (kpi: string) => selectedKpiList.includes(kpi);
  const title = t("kpiSelectorTitle", defaultContent["kpiSelectorTitle"], {
    totalSelected: `${selectedKpiList.length}`,
    maxSelection: `${maxSelectionsAllowed}`,
  });

  const canExceedMaxSelection = availableKpis.length > maxSelectionsAllowed;
  const [hasExceededMaxSelection, setHasExceededMaxSelection] = useState(
    canExceedMaxSelection && selectedKpiList.length >= maxSelectionsAllowed,
  );
  const toastMessage = t("keyMetricsToastMessage", defaultContent["keyMetricsToastMessage"]).toString();
  const toastMessageTimeout = 5000;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const resetKpiSelection = () => {
    setSelectedKpiList(selectedKpis);
    setItems(availableKpis);
  };

  const handleOnClose = () => {
    resetKpiSelection();
    onCloseCallbackFn();
  };

  useEffect(() => {
    setSelectedKpiList(selectedKpis);
    setItems(availableKpis);
  }, [selectedKpis, availableKpis]);

  useEffect(() => {
    // This reset must run whenever the modal closes regardless of canExceedMaxSelection -
    // otherwise reports where max === total KPI count (Renewals, CVR, etc) never get it, since
    // the early return below exits before reaching it.
    if (!show) {
      resetKpiSelection();
    }

    if (!canExceedMaxSelection) {
      setHasExceededMaxSelection(false);
      return;
    }

    if (show && hasExceededMaxSelection) {
      const timer = setTimeout(() => {
        setHasExceededMaxSelection(false);
      }, toastMessageTimeout);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, hasExceededMaxSelection, canExceedMaxSelection]);

  const renderWarningMessage = () => {
    if (!canExceedMaxSelection) {
      return <></>;
    }
    return hasExceededMaxSelection ? (
      <div className="kpi-selector__message-container" role="alert">
        <div className="kpi-selector__message">
          <div>{toastMessage}</div>
          <Button className="custom-close-button" onClick={() => setHasExceededMaxSelection(false)}>
            <IconCloseThick height="0.875rem" width="0.625rem" theme={"warning"}></IconCloseThick>
          </Button>
        </div>
      </div>
    ) : (
      <></>
    );
  };

  useEffect(() => {
    if (show) {
      return;
    }
    setHasExceededMaxSelection(canExceedMaxSelection && selectedKpiList.length >= maxSelectionsAllowed);
  }, [canExceedMaxSelection, maxSelectionsAllowed, selectedKpiList.length, selectedKpis, show]);

  const handleCheckboxChange = (kpi: string, e: React.KeyboardEvent) => {
    if (!disableScrollLock) setFocusedElement(e.target as HTMLElement);
    const updatedSelection = isKpiSelected(kpi)
      ? selectedKpiList.filter((selectedKpi) => selectedKpi !== kpi)
      : [...selectedKpiList, kpi];

    setSelectedKpiList(updatedSelection);
    setHasExceededMaxSelection(canExceedMaxSelection && updatedSelection.length >= maxSelectionsAllowed);

    track(
      TEALIUM.U_TAG_METHOD.LINK,
      TEALIUM.EVENT_NAME.CLICK_ACTION,
      TEALIUM.CATEGORY.MY_PERFORMANCE_DASH,
      "customize_kpi_menu",
      "",
      `${isKpiSelected(kpi) ? "unselected" : "selected"}_kpi_${kpi}`,
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOnClose();
    }
  };

  const isCheckboxDisabled = (kpi: string) => selectedKpiList.length >= maxSelectionsAllowed && !isKpiSelected(kpi);

  const renderHeader = () => {
    return (
      <>
        <div className="kpi-selector__header">
          <div className="kpi-selector__titleRow">
            <h3 id="modal-header" className="kpi-selector__title">
              {title}
            </h3>
            <div>
              <Button
                ref={closeButtonRef}
                className="leaf-panel__closeBtn"
                aria-label={`${t("kpiSelectorCloseArialLabel", defaultContent["kpiSelectorCloseArialLabel"])}`}
                onClick={() => handleOnClose()}
                onKeyDown={handleKeyPress}
              >
                <IconClose width={"0.8125rem"} height={"1.25rem"} />
              </Button>
            </div>
          </div>
          {description && <div className="kpi-selector__description">{description}</div>}
        </div>
      </>
    );
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveId("");
  };

  const dropAnimationConfig: DropAnimation = {
    duration: 200,
    sideEffects: defaultDropAnimationSideEffects({
      styles: {
        active: {
          opacity: "0.5",
        },
      },
    }),
  };

  const renderCheckboxList = () => {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          <div className="kpi-selector__list">
            {items.map((kpi: string) => (
              <SortableKpiItem
                kpiID={kpi}
                key={kpi}
                checked={isKpiSelected(kpi)}
                disabled={isCheckboxDisabled(kpi)}
                onChange={handleCheckboxChange}
                textOverride={textOverride}
                personalPVRequirementCode={personalPVRequirementCode}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay adjustScale={true} dropAnimation={dropAnimationConfig}>
          {activeId ? (
            <KpiItem
              kpi={activeId}
              checked={isKpiSelected(activeId)}
              disabled={false}
              isDragging={true}
              onChange={handleCheckboxChange}
              textOverride={textOverride}
              personalPVRequirementCode={personalPVRequirementCode}
            />
          ) : null}
        </DragOverlay>
      </DndContext>
    );
  };

  const renderFooter = () => {
    const handleButtonClick = () => {
      const orderedSelectedKpis = items.filter((item) => selectedKpiList.includes(item));
      onSubmitFn(orderedSelectedKpis);
    };
    return (
      <div className="kpi-selector__buttons">
        <Button data-testid={"kpi-selector__cancel"} className="kpi-selector__cancel" onClick={() => handleOnClose()}>
          <Trans>{t("kpiSelectorModalCancel", defaultContent["kpiSelectorModalCancel"])}</Trans>
        </Button>
        <Button
          data-testid={"kpi-selector__apply"}
          className="kpi-selector__apply"
          onClick={() => handleButtonClick()}
          disabled={isApplyButtonDisabled}
        >
          <Trans>{t("kpiSelectorModalApply", defaultContent["kpiSelectorModalApply"])}</Trans>
        </Button>
      </div>
    );
  };

  useLayoutEffect(() => {
    const isMobile = Number(width) <= Number(breakpoints["tabletSmall"]);
    if (closeButtonRef.current && show && !isMobile) {
      closeButtonRef.current.focus();
    }
    setIsMobileDevice(isMobile);
  }, [show, width]);

  useEffect(() => {
    if (show && !isMobileDevice && !disableScrollLock) {
      if (modalRef.current && modalRef.current.scrollIntoView) {
        modalRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      // Use overflow:hidden instead of position:fixed to prevent scroll without
      // causing layout shifts or disrupting Lit web component rendering.
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = "hidden";
      // Compensate for scrollbar width disappearing so the page doesn't jump
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`;
      }
      return () => {
        document.body.style.overflow = "";
        document.body.style.paddingRight = "";
      };
    }
  }, [show, isMobileDevice]);

  const isApplyButtonDisabled = selectedKpiList.length < minSelectionsAllowed;

  return show ? (
    <div className="kpi-selector" data-testid="kpi-selector" ref={modalRef}>
      {isMobileDevice ? (
        <LeafPanel
          applyScroll={true}
          id="kpi-tracking-filter-modal"
          {...{
            show: isMobileDevice,
            onCloseClick: handleOnClose,
            title,
            subheader: description ? (
              <>
                <div className="kpi-selector__description">{description}</div>
                {renderWarningMessage()}
              </>
            ) : (
              <> {renderWarningMessage()}</>
            ),
            footer: renderFooter(),
          }}
        >
          {renderCheckboxList()}
        </LeafPanel>
      ) : (
        <FocusTrap
          aria-modal={true}
          tabIndex={0}
          role="dialog"
          aria-labelledby="modal-header"
          handleOnClose={handleOnClose}
          focusedElement={focusedElement}
        >
          {renderHeader()}
          {renderWarningMessage()}
          {renderCheckboxList()}
          {renderFooter()}
        </FocusTrap>
      )}
    </div>
  ) : (
    <></>
  );
};
